import {
  List,
  Text,
  ThemeIcon,
  Divider,
  Flex,
  Space,
  Paper,
  Alert,
} from "@mantine/core"
import { useSuspenseQueryPost } from "../../../typed-client/hooks/use-suspense-query-post"
import { IconCheck } from "@tabler/icons-react"
import { useBreakpoints } from "../../../utils/use-breakpoints"
import { Period } from "../../../@client/Absents"
import { ActivityItem } from "./item"
import { useTranslations } from "../../../utils/use-t"
import { ActivitiesAndBasicCastsInPeriod } from "../../../@client/Theasoft_Absences"

export function ActivitiesAndBasicCastsForCurrentUser({
  period,
}: {
  period: Period
}) {
  const { data } = useSuspenseQueryPost(
    "/api/Absence/GetActivitiesAndBasicCastsInPeriod",
    {
      period,
    }
  )

  const {
    appServer: { t },
  } = useTranslations()

  if (!data) {
    return (
      <Alert title={t("Warning")} color="yellow">
        {t(
          "MyAbsences_NewLeaveRequest_ActivitiesAndBasicCastsInPeriod_NotFound"
        )}
      </Alert>
    )
  }

  const show = data.showIfEmpty
    ? true
    : data.groupedActivities.length === 0
      ? false
      : true
  return show ? (
    <Paper p="md" withBorder={true}>
      <Text>{t("MyAbsences_NewLeaveRequest_AlreadyAnnouncedCasts")}</Text>
      <Space my="xs" />
      <ActivitiesAndBasicCasts data={data} />
      <Text c="yellow">
        <br />
        {t("MyAbsences_NewLeaveRequest_NotYetAnnouncedCasts")}
      </Text>
    </Paper>
  ) : null
}

export function ActivitiesAndBasicCasts({
  data,
}: {
  data: ActivitiesAndBasicCastsInPeriod
}) {
  const ts = useTranslations()

  const { xs, sm } = useBreakpoints()
  const overlappingActivities =
    data.groupedActivities.find((v) => v[0].Case === "Overlapping")?.[1] || []

  const nonOverlappingActivities =
    data.groupedActivities.find((v) => v[0].Case === "NotOverlapping")?.[1] ||
    []

  const samyAsReturnDayActivities =
    data.groupedActivities.find((v) => v[0].Case === "SameAsReturnDay")?.[1] ||
    []

  return (
    <List spacing="xs" size="xs" center>
      <ActivityItem key={"startOfAbsence"} activity={data.startOfAbsence} />
      <List
        key={"list"}
        pl="xl"
        spacing="xs"
        size="xs"
        center
        my="md"
        styles={{
          root: {
            borderLeft: "2px solid #e0e0e0",
            marginLeft: 12,
          },
        }}
      >
        {overlappingActivities.length === 0 ? (
          <React.Fragment key={"no-activities"}>
            <Flex key={"no-activities-group"}>
              <ThemeIcon
                component="span"
                variant="light"
                color="green"
                radius="xl"
                size="xs"
                mt={4}
                mr={8}
              >
                <IconCheck />
              </ThemeIcon>
              <Text span c="dimmed">
                {ts.absences.t("NoActivitiesInAbsence")}
              </Text>
            </Flex>
          </React.Fragment>
        ) : null}

        {overlappingActivities.map((tuple, i) => (
          <React.Fragment key={i}>
            <ActivityItem key={"overlap" + tuple.activityId} activity={tuple} />
            {sm ? (
              <Divider key={"overlap" + tuple.activityId + "divider"} my="xs" />
            ) : null}
          </React.Fragment>
        ))}
      </List>
      <ActivityItem
        key={"overlapping" + data}
        activity={data.endOfAbsence}
        highlightTime={samyAsReturnDayActivities.length > 0}
      />

      {samyAsReturnDayActivities.length > 0 ? (
        <Text key="same-day-info" mt="xl">
          {ts.absences.t("CastsAtDayOfReturn")}
        </Text>
      ) : null}
      {samyAsReturnDayActivities.map((v, i) => (
        <React.Fragment key={"same-day-activities-" + i}>
          <ActivityItem key={"sameAsReturn" + v.activityId} activity={v} />
          {sm || xs ? (
            <Divider key={"sameAsReturn" + v.activityId + "divider"} my="xs" />
          ) : null}
        </React.Fragment>
      ))}
      {nonOverlappingActivities.length > 0 ? (
        <Text key={"non-overlapping-activities"} mt="xl">
          {ts.absences.t("PremiersAfterAbsence")}
        </Text>
      ) : null}
      {nonOverlappingActivities.map((v, i) => (
        <React.Fragment key={"non-overlapping-" + i}>
          <ActivityItem key={"nonOverlapping" + v.activityId} activity={v} />
          {sm || xs ? (
            <Divider
              key={"nonOverlapping" + v.activityId + "divider"}
              my="xs"
            />
          ) : null}
        </React.Fragment>
      ))}
    </List>
  )
}
